// =============================================================================
// components/community/SegmentedTabs.tsx — the Channels | Key Info | Preferences
// segmented row from the reference community interior (IMG_5877): flat labels
// with a purple underline pill under the active tab. Kept generic (keys +
// labels) so the same row can serve other community surfaces.
// =============================================================================
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { colors, spacing, fonts } from '../../constants/theme';

export interface SegmentedTab<K extends string = string> {
  key: K;
  label: string;
}

export default function SegmentedTabs<K extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: SegmentedTab<K>[];
  active: K;
  onChange: (key: K) => void;
}) {
  return (
    <View style={styles.row} accessibilityRole="tablist">
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <TouchableOpacity
            key={t.key}
            style={styles.tab}
            onPress={() => onChange(t.key)}
            activeOpacity={0.7}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={`${t.label} tab`}
          >
            <View style={styles.tabInner}>
              <Text style={[styles.label, isActive && styles.labelActive]} numberOfLines={1}>
                {t.label}
              </Text>
              <View style={[styles.underline, isActive && styles.underlineActive]} />
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  // Reference (IMG_5877): tabs share the full row width evenly; the purple
  // underline hugs the label width of the active tab.
  row: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(0,0,0,0.06)',
  },
  tab: { flex: 1, alignItems: 'center', paddingTop: spacing.md },
  // The underline hugs the label width (reference IMG_5877): both live in an
  // auto-width column so the accent bar tracks the text, not the full segment.
  tabInner: { alignItems: 'center' },
  label: { ...fonts.semibold, fontSize: 14, color: colors.muted, paddingBottom: spacing.sm },
  labelActive: { color: colors.ink, ...fonts.bold },
  underline: { height: 3, alignSelf: 'stretch', borderRadius: 2, backgroundColor: 'transparent' },
  underlineActive: { backgroundColor: colors.primary },
});
